import type { CleaningTaskQueueResponse } from '@/shared/api';
import type { PhotoSlot } from '../capture/photoSlotModel';
import type { PlanStep } from '../my-tasks/myTasksModel';
import { windowLabel } from '../my-tasks/taskLabels';

type Task = CleaningTaskQueueResponse;

export type WorkStep = Exclude<PlanStep, 'continue'>;

/**
 * Der Schritt unten in der Aufgabe. Hier zählt `canMutate` allein: die Leitung darf eine fremde
 * Aufgabe starten oder ohne Fotos abschließen, wie bisher in der Arbeitsansicht. Wer zuteilt,
 * übernimmt dabei nicht (`manage`): eine freie Aufgabe wird gestartet, nicht sich selbst
 * zugewiesen – so wie das frühere „Starten“. Der Server prüft dasselbe.
 */
export function workStep(task: Task, manage: boolean): WorkStep | null {
  if (task.status === 'Completed' || task.status === 'Issue' || task.status === 'Cancelled')
    return null;
  const withoutPhotos = task.photoSlotCount === 0;
  if (task.canClaim && !manage) return withoutPhotos ? 'finish' : 'claim';
  if (!task.canMutate) return null;
  if (withoutPhotos) return 'finish';
  return task.status === 'Pending' ? 'start' : null;
}

/** „Noch 2 Fotos: Bad, Balkon“ — die offenen Räume in der Reihenfolge der Raumliste. */
export function openRoomsLine(slots: PhotoSlot[]): string | null {
  const open = slots.filter((slot) => !slot.photo).map((slot) => slot.item.text);
  if (open.length === 0) return null;
  return `Noch ${open.length} ${open.length === 1 ? 'Foto' : 'Fotos'}: ${open.join(', ')}`;
}

/** Der Kopfsatz: Tag (wenn nicht heute), läuft seit oder Fenster, nächste Anreise, Zeitzone. */
export function workFacts(
  task: Task,
  parts: { day: string | null; startedAt: string | null; arrival: string | null },
): string {
  return [
    parts.day,
    task.status === 'Cancelled'
      ? 'Entfällt – die Buchung dahinter gibt es nicht mehr'
      : parts.startedAt
        ? `Läuft seit ${parts.startedAt}`
        : windowLabel(task),
    parts.arrival,
    task.timeZoneDataQualityCode ? 'Zeitzone prüfen' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
